import { type FC, useState, useEffect, useRef, useContext, Suspense } from 'react';
import { graphql, useRelayEnvironment, useRefetchableFragment } from 'react-relay';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers';
import { getBuyerId } from 'dibs-cookie-jar';
import nftItemUpdateSubscription from './subscriptions/nftItemUpdate';
import { useRecentHistory } from '../hooks/useRecentHistory';
import { NftPdpBreadcrumbs } from './NftPdpBreadcrumbs/NftPdpBreadcrumbs';
import { NftPdpSharingIcons } from './NftPdpSharingIcons/NftPdpSharingIcons';
import { NftPdpMedia } from './NftPdpMedia/NftPdpMedia';
import { NftPdpSidebar } from './NftPdpSidebar/NftPdpSidebar';
import { NftPdpAbout } from './NftPdpAbout/NftPdpAbout';
import { NftPdpHistory } from './NftPdpHistory/NftPdpHistory';
import { NftPdpTabs } from './NftPdpTabs/NftPdpTabs';
import { NftPdpCreator } from './NftPdpCreator/NftPdpCreator';
import { NftPdpCreatorItems } from './NftPdpCreator/NftPdpCreatorItems';
import { NftPdpAdditionalContent } from './NftPdpAdditionalContent/NftPdpAdditionalContent';
import { NftPdpExhibitionNotes } from './NftPdpExhibitionNotes/NftPdpExhibitionNotes';
import { NftPdpContractContextProvider } from './NftPdpContractContext/NftPdpContractContext';
import { NftPdpExhibitions } from './NftPdpExhibitions/NftPdpExhibitions';
import { NftPdpLiveDetailsRefetchWrapper } from './NftPdpLiveDetails/NftPdpLiveDetailsRefetchWrapper';
import { ServerVarsContext } from '../utils/ServerVarsContext';
import {
    type NftPdpRespLayout_viewer$key,
    type NftPdpRespLayout_viewer$data,
} from './__generated__/NftPdpRespLayout_viewer.graphql';
import { type NftPdpRespLayoutRefetchQuery } from './__generated__/NftPdpRespLayoutRefetchQuery.graphql';
import { type Disposable } from 'relay-runtime';
import { NftFaq } from 'dibs-nft-faq/exports/NftFaq';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import styles from './NftPdpRespLayout.scss';

type Props = {
    viewer: NftPdpRespLayout_viewer$key;
};
type NftItem = ArrayElement<NftPdpRespLayout_viewer$data['nftItems']>;

export const NftPdpRespLayout: FC<Props> = ({ viewer: viewerRef }) => {
    const { isTablet, isMobile } = useContext(ServerVarsContext);
    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true);
    }, []);
    const deviceType = (isTablet && 'tablet') || (isMobile && 'mobile') || 'desktop';
    const itemUpdateRef = useRef<Disposable | null>(null);
    const environment = useRelayEnvironment();
    const { addItemToHistory } = useRecentHistory();
    const [viewer, refetch] = useRefetchableFragment<
        NftPdpRespLayoutRefetchQuery,
        NftPdpRespLayout_viewer$key
    >(
        graphql`
            fragment NftPdpRespLayout_viewer on Viewer
            @refetchable(queryName: "NftPdpRespLayoutRefetchQuery") {
                ...NftPdpSidebar_viewer
                ...NftPdpHistory_viewer
                ...NftPdpTabs_viewer
                ...NftPdpCreator_viewer
                ...NftPdpSharingIcons_viewer
                ...NftPdpCreatorItems_viewer
                nftItems(nftIds: [$nftId], isDraft: $isDraft) {
                    serviceId
                    mostRecentAuction {
                        status
                    }
                    exhibitions {
                        __typename
                    }
                    creatorAddress {
                        ...NftPdpCreatorItems_creatorAddress
                            @arguments(itemCount: $creatorItemCount)
                        publicProfile {
                            profileName
                        }
                    }
                    isERC1155
                    ...NftPdpAbout_nftItem
                    ...NftPdpSharingIcons_nftItem
                    ...NftPdpMedia_nftItem
                    ...NftPdpSidebar_nftItem
                    ...NftPdpContractContext_nftItem
                    ...NftPdpHistory_nftItem
                    ...NftPdpTabs_nftItem
                    ...NftPdpCreator_nftItem
                    ...NftPdpAdditionalContent_nftItem
                    ...NftPdpExhibitionNotes_nftItem
                    ...NftPdpExhibitions_nftItem
                }
                showExhibitionNotes: featureFlag(feature: "nftExhibitionNotes")
                ...NftPdpLiveDetailsRefetchWrapper_viewer
            }
        `,
        viewerRef
    );

    const nftItem: NftItem = viewer?.nftItems?.[0] || null;
    const { showExhibitionNotes } = viewer;
    const exhibitionsCount = nftItem?.exhibitions?.length || 0;

    useEffect(() => {
        const buyerId = getBuyerId(document.cookie);
        if (buyerId) {
            refetch({ userId: buyerId, fetchUser: true });
        }
        addLoginHandlers(() => {
            const userId = getBuyerId(document.cookie);
            if (userId) {
                refetch({ userId: buyerId, fetchUser: true });
            }
        });
    }, [refetch]);

    useEffect(() => {
        if (!itemUpdateRef.current && nftItem && nftItem.serviceId) {
            itemUpdateRef.current = nftItemUpdateSubscription(environment, {
                itemId: nftItem.serviceId,
            });
        }
        return () => {
            if (itemUpdateRef.current) {
                itemUpdateRef.current.dispose();
            }
        };
    }, [environment, nftItem]);

    useEffect(() => {
        if (nftItem && nftItem.serviceId) {
            addItemToHistory(nftItem.serviceId);
        }
    }, [nftItem, addItemToHistory]);

    if (!nftItem) {
        return null;
    }

    return (
        <NftPdpContractContextProvider nftItem={nftItem}>
            <div className={styles.breadcrumbRow}>
                <NftPdpBreadcrumbs />
            </div>
            <article>
                {/* Share Icons, Media, Sidebar */}
                <div className={styles.topContent}>
                    {/* Sidebar comes first because it contains the h1 for the page */}
                    {isMobile ? (
                        <>
                            <NftPdpSidebar viewer={viewer} nftItem={nftItem} />
                            <NftPdpMedia nftItem={nftItem} />
                        </>
                    ) : (
                        <div className={styles.desktopMedia}>
                            <NftPdpMedia nftItem={nftItem} />
                        </div>
                    )}
                    <NftPdpSharingIcons nftItem={nftItem} viewer={viewer} />
                </div>
                {/* NFT Metadata */}
                {!isMobile && (
                    <div className={styles.desktopGrid}>
                        <div className={styles.gridLeftContainer}>
                            <NftPdpAbout nftItem={nftItem} />
                            <NftPdpCreator viewer={viewer} nftItem={nftItem} />
                            {showExhibitionNotes ? (
                                <NftPdpExhibitionNotes nftItem={nftItem} />
                            ) : (
                                <NftPdpAdditionalContent nftItem={nftItem} />
                            )}
                        </div>
                        <div>
                            {isClient && (
                                <Suspense fallback={''}>
                                    <NftPdpLiveDetailsRefetchWrapper viewer={viewer} />
                                </Suspense>
                            )}
                            {!nftItem?.isERC1155 ? (
                                <NftPdpHistory
                                    nftItem={nftItem}
                                    viewer={viewer}
                                    hasEditions={false}
                                />
                            ) : (
                                <NftPdpTabs nftItem={nftItem} viewer={viewer} />
                            )}
                        </div>
                    </div>
                )}
                {/* Mobile layout */}
                {isMobile && (
                    <>
                        <NftPdpAbout nftItem={nftItem} />
                        <div className={styles.historyAndCreator}>
                            <div>
                                {!nftItem?.isERC1155 ? (
                                    <NftPdpHistory
                                        nftItem={nftItem}
                                        viewer={viewer}
                                        hasEditions={false}
                                    />
                                ) : (
                                    <NftPdpTabs nftItem={nftItem} viewer={viewer} />
                                )}
                                {showExhibitionNotes ? (
                                    <NftPdpExhibitionNotes nftItem={nftItem} />
                                ) : (
                                    <NftPdpAdditionalContent nftItem={nftItem} />
                                )}
                            </div>
                            <NftPdpCreator viewer={viewer} nftItem={nftItem} />
                        </div>
                    </>
                )}
                <div className={styles.bottomContainer}>
                    {!isMobile && (
                        <NftPdpCreatorItems
                            viewer={viewer}
                            creatorAddress={nftItem?.creatorAddress}
                        />
                    )}
                    {exhibitionsCount > 0 && <NftPdpExhibitions nftItem={nftItem} />}
                </div>
                <NftFaq hasBrowseLink deviceType={deviceType} />
            </article>
        </NftPdpContractContextProvider>
    );
};
